/**
 * Features Data for Sortio
 * Detailed feature pages for Sortio capabilities
 */

export const featuresData = [
  {
    id: 'smart-folders',
    slug: 'smart-folders',
    title: 'Smart Folders',
    category: 'Features',
    shortDescription: 'Automatically organize files as they arrive with intelligent folder monitoring.',
    definition: 'Smart Folders are designated directories that Sortio continuously monitors, automatically sorting new files based on your configured rules and AI preferences as they arrive.',
    whatIs: 'Smart Folders transform any folder on your computer into an intelligent inbox that automatically organizes files as they arrive. Unlike traditional folders that simply store files, Smart Folders actively process and categorize new additions based on your preferences. When you designate a folder as "smart," Sortio watches it for changes and applies your sorting rules automatically—no manual intervention required.',
    howItWorks: 'When you designate a folder as a Smart Folder, Sortio registers it for continuous monitoring. Each time a new file is added to that folder, Sortio analyzes it using your configured sorting preferences and AI-powered categorization. The file is then automatically moved to the appropriate destination folder based on file type, content, metadata, or your custom rules. This happens in the background without interrupting your workflow. You can have multiple Smart Folders with different configurations, each handling files according to its specific rules.',
    keyCapabilities: [
      'Real-time folder monitoring for new files',
      'Automatic categorization based on file type and content',
      'Custom rules for specific file patterns',
      'Works in background without interrupting your workflow',
      'Multiple Smart Folders with different configurations',
      'Optional file renaming during sort operations',
      'Integration with activity logging for full audit trail'
    ],
    gettingStarted: [
      'Open Sortio and navigate to the Smart Folders section',
      'Click "Add Smart Folder" and select any folder on your computer',
      'Choose a sorting method: by type, date, content, or custom rules',
      'Set destination folders for each category',
      'Optionally enable file renaming during sort',
      'Save and your Smart Folder begins monitoring immediately'
    ],
    useCases: [
      'Downloads Folder: Automatically sort new downloads into Documents, Images, Videos, and more as they arrive',
      'Email Attachments: Point a Smart Folder at your email attachment directory to auto-organize incoming files by sender or project',
      'Scan Inbox: Automatically categorize and file scanned documents as they come off your scanner',
      'Project Intake: Create a client intake folder that automatically files new documents into the correct project structure',
      'Desktop Cleanup: Turn your Desktop into a Smart Folder that keeps it tidy by moving files to appropriate locations'
    ],
    configurationOptions: [
      {
        name: 'Sorting Method',
        description: 'Choose between file type, date, content analysis, or custom rules for categorization'
      },
      {
        name: 'Destination Mapping',
        description: 'Define where files of each category should be moved'
      },
      {
        name: 'File Renaming',
        description: 'Optionally rename files according to a pattern during sorting'
      },
      {
        name: 'Exclusion Patterns',
        description: 'Specify file types or patterns to ignore'
      },
      {
        name: 'Scheduling',
        description: 'Set monitoring frequency or enable real-time monitoring'
      }
    ],
    bestPractices: [
      'Start with one Smart Folder (like Downloads) before expanding',
      'Use clear, descriptive destination folder names',
      'Review sorted files periodically to refine your rules',
      'Create a "Review" destination for files that need manual attention',
      'Enable activity logging to track what gets sorted where',
      'Back up important files before enabling Smart Folders on them'
    ],
    relatedTerms: ['automated-file-sorting', 'folder-structure', 'file-categorization'],
    lastUpdated: '2025-01-15',
    faq: [
      {
        question: 'Will Smart Folders work when my computer is asleep?',
        answer: 'Smart Folders monitor for changes when Sortio is running. If your computer sleeps, monitoring pauses and resumes when you wake it. Any files added during sleep will be processed once monitoring resumes.'
      },
      {
        question: 'Can I pause a Smart Folder temporarily?',
        answer: 'Yes, you can disable monitoring for any Smart Folder without removing it. This is useful when you want to manually manage files temporarily or make bulk changes to a folder.'
      },
      {
        question: 'What happens if a file doesn\'t match any rule?',
        answer: 'You can configure a default destination for unmatched files, or leave them in place for manual review. We recommend creating a "Needs Review" destination for files that don\'t fit your categories.'
      },
      {
        question: 'Do Smart Folders work with content analysis?',
        answer: 'Yes. When content analysis is enabled, Smart Folders can sort files based on their content, not just filename or type. Content analysis only occurs when you explicitly enable the content sorting toggle.'
      }
    ]
  },
  {
    id: 'offline-mode',
    slug: 'offline-mode',
    title: 'Offline Mode',
    category: 'Security',
    shortDescription: 'Process files locally without any cloud connectivity for maximum privacy.',
    definition: 'Offline Mode ensures your files never leave your device by processing all AI analysis and file organization entirely on your local machine, with no external server connections.',
    whatIs: 'Offline Mode is a privacy-first feature that allows Sortio to function completely independently of the internet. When enabled, all file analysis, AI processing, and organization happens entirely on your device. No file data, metadata, or usage information is transmitted externally. This makes Sortio suitable for air-gapped environments, confidential documents, and users who prioritize maximum privacy.',
    howItWorks: 'When Offline Mode is enabled, Sortio uses locally-installed AI models to analyze and categorize your files. These models run entirely on your device\'s CPU/GPU, processing files without any network requests. The initial model download is a one-time requirement (done while online), after which Sortio can operate indefinitely without internet access. Offline mode processes files locally on your device without cloud connectivity.',
    keyCapabilities: [
      'Complete local processing with no cloud connectivity',
      'On-device AI models for file analysis',
      'No data sent to external servers',
      'Full functionality without internet',
      'Ideal for air-gapped environments',
      'Same sorting capabilities as online mode',
      'Works with all file types and sorting methods'
    ],
    gettingStarted: [
      'Go to Settings > Privacy in Sortio',
      'Toggle "Offline Mode" to enabled',
      'Download the required local AI models (one-time, requires internet)',
      'Choose your preferred model size based on accuracy vs. speed needs',
      'Once downloaded, Offline Mode works immediately',
      'Verify offline status with the indicator in the status bar'
    ],
    useCases: [
      'Confidential Documents: Organize sensitive legal, medical, or financial documents with no data leaving your device',
      'Air-Gapped Systems: Use Sortio on computers that cannot connect to the internet for security reasons',
      'Travel Workflow: Organize files while traveling without relying on internet connectivity',
      'Corporate Compliance: Meet strict data residency or security requirements that prohibit cloud processing',
      'Personal Privacy: For users who prefer to keep all file operations entirely local'
    ],
    configurationOptions: [
      {
        name: 'Model Size',
        description: 'Choose between smaller, faster models or larger, more accurate models based on your needs'
      },
      {
        name: 'Auto-Enable',
        description: 'Automatically switch to offline mode when no internet is detected'
      },
      {
        name: 'Model Updates',
        description: 'Control when and how local models are updated (requires temporary internet access)'
      },
      {
        name: 'Processing Priority',
        description: 'Balance between processing speed and system resource usage'
      }
    ],
    bestPractices: [
      'Download offline models before traveling or going offline',
      'Choose the model size that balances accuracy with your system resources',
      'Keep models updated periodically for best results',
      'Use offline mode for any sensitive or confidential documents',
      'Verify the offline indicator is active before processing sensitive files'
    ],
    relatedTerms: ['file-encryption', 'secure-file-archiving'],
    lastUpdated: '2025-01-15',
    faq: [
      {
        question: 'How accurate is offline mode compared to online?',
        answer: 'Offline mode uses optimized local models that provide excellent accuracy for most use cases. For specialized or complex sorting tasks, online mode may offer slightly better results, but most users won\'t notice a difference in typical file organization scenarios.'
      },
      {
        question: 'How much storage do the offline models require?',
        answer: 'The base offline model requires approximately 500MB of storage. Larger, more accurate models can range from 1-3GB. You can choose the model size that fits your needs and available storage.'
      },
      {
        question: 'Can I switch between online and offline modes?',
        answer: 'Yes, you can toggle between modes at any time in Settings. Your sorting preferences and Smart Folder configurations work in both modes.'
      },
      {
        question: 'Is my data completely private in offline mode?',
        answer: 'Yes. In offline mode, Sortio makes no network connections for file processing. Your files, metadata, and sorting patterns remain entirely on your device.'
      }
    ]
  },
  {
    id: 'activity-logging',
    slug: 'activity-logging',
    title: 'Activity Logging',
    category: 'Security',
    shortDescription: 'Track every file operation with detailed, secure activity logs.',
    definition: 'Activity Logging provides a comprehensive audit trail of all file operations performed by Sortio, including source locations, destinations, timestamps, and the rules or prompts that triggered each action.',
    whatIs: 'Activity Logging is Sortio\'s built-in accountability feature that records every file operation in a secure, searchable log. Every time Sortio moves, renames, or organizes a file, the operation is documented with full details. This creates a complete history of your file organization activities, essential for compliance requirements, troubleshooting, and understanding how your files have been organized over time.',
    howItWorks: 'When Sortio performs any file operation, it creates a log entry containing: the original file location, the destination location, the timestamp, the rule or prompt that triggered the action, and file metadata before and after the operation. Logs are stored securely on your device with optional encryption. You can search, filter, and export logs at any time. Sortio maintains detailed logs of all file operations including what files were moved, when, and where.',
    keyCapabilities: [
      'Detailed logs of all file movements',
      'Timestamp and location tracking for every operation',
      'Before and after states recorded',
      'Searchable log history',
      'Export logs for compliance reporting (JSON or CSV)',
      'Secure, encrypted log storage',
      'Customizable retention periods'
    ],
    gettingStarted: [
      'Activity logging is enabled by default—no setup required',
      'Access logs via the Activity tab in Sortio',
      'Use the search bar to find specific files or operations',
      'Filter by date range, operation type, or folder',
      'Export logs using the Export button',
      'Adjust retention settings in Settings > Logging'
    ],
    useCases: [
      'Compliance Requirements: Maintain audit trails required for legal, financial, or healthcare compliance',
      'Undo Operations: Review logs to understand what was changed and restore files to previous locations if needed',
      'Workflow Analysis: Analyze organization patterns to optimize your folder structure and sorting rules',
      'Troubleshooting: Understand why a file ended up in a particular location',
      'Team Accountability: Track file organization activities in shared environments'
    ],
    configurationOptions: [
      {
        name: 'Retention Period',
        description: 'Set how long logs are kept: 30, 90, 365 days, or forever'
      },
      {
        name: 'Detail Level',
        description: 'Choose between basic logging (operations only) or verbose (includes metadata changes)'
      },
      {
        name: 'Export Format',
        description: 'Export logs as JSON for programmatic access or CSV for spreadsheet analysis'
      },
      {
        name: 'Log Encryption',
        description: 'Enable encryption for stored logs containing sensitive file paths'
      }
    ],
    bestPractices: [
      'Set retention periods appropriate for your compliance requirements',
      'Export logs regularly if you need long-term records',
      'Use verbose logging for important folders or sensitive documents',
      'Review logs periodically to refine your sorting rules',
      'Enable log encryption if file paths contain sensitive information'
    ],
    relatedTerms: ['file-audit', 'file-location-tracking', 'document-management-system'],
    lastUpdated: '2025-01-15',
    faq: [
      {
        question: 'Do logs include the actual file contents?',
        answer: 'No. Logs only contain metadata about operations: file paths, timestamps, and operation types. File contents are never logged, keeping log files small and maintaining privacy.'
      },
      {
        question: 'Can I use logs to undo a sorting operation?',
        answer: 'Logs show you exactly what was moved and where. While logs themselves don\'t provide automatic undo, Sortio backs up your files before making any changes, allowing you to revert to the original organization at any time from within the app.'
      },
      {
        question: 'How much storage do logs use?',
        answer: 'Logs are very compact, typically a few KB per operation. A year of active use might generate 10-50MB of logs depending on your activity level.'
      },
      {
        question: 'Can I share logs with my IT department?',
        answer: 'Yes. Logs can be exported as JSON or CSV files, making them easy to share, import into other systems, or analyze with external tools.'
      }
    ]
  },
  {
    id: 'recursive-sorting',
    slug: 'recursive-sorting',
    title: 'Recursive Subfolder Sorting',
    category: 'Features',
    shortDescription: 'Sort files across nested folder structures with intelligent exclusion patterns.',
    definition: 'Recursive Subfolder Sorting allows Sortio to scan and organize files within nested directory structures, processing files from subfolders while respecting exclusion patterns for system and project folders.',
    whatIs: 'Recursive Subfolder Sorting extends Sortio\'s organization capabilities to handle complex, nested folder structures. Instead of sorting only files in a single folder, you can now sort files across an entire directory tree. This is especially useful for project folders, archive directories, and any situation where files are scattered across multiple levels of subfolders. Intelligent exclusion patterns automatically skip system folders, version control directories, and dependency folders.',
    howItWorks: 'When you enable the "Include subfolders" toggle, Sortio recursively scans all nested directories within your selected folder. Before sorting begins, you\'ll see a preview of how many files were found. You can configure exclusion patterns to skip folders like node_modules, .git, __pycache__, and other directories you don\'t want to process. Files from subfolders are moved to their new destinations while maintaining proper path handling. A file limit ensures performance remains optimal even for large directory structures.',
    keyCapabilities: [
      'Scan nested directories to any depth',
      'Preview file count before sorting begins',
      'Configurable exclusion patterns for folders to skip',
      'Default exclusions for common system folders (.git, node_modules, etc.)',
      'Proper path handling for files from subdirectories',
      'Performance-optimized with configurable file limits',
      'Works with all sorting methods and prompts'
    ],
    gettingStarted: [
      'Select a folder containing nested subfolders',
      'Toggle "Include subfolders" to enable recursive scanning',
      'Review the file count preview to ensure it matches expectations',
      'Add any custom exclusion patterns for folders to skip',
      'Enter your sorting prompt as usual',
      'Preview and apply the sort—files from all levels will be organized'
    ],
    useCases: [
      'Project Cleanup: Organize scattered files across a project directory while skipping node_modules, .git, and build folders',
      'Archive Organization: Sort years of accumulated files in nested archive folders into a clean, flat structure',
      'Download Consolidation: Process a Downloads folder with subfolders from extracted archives',
      'Photo Libraries: Organize photos scattered across date-based subfolder structures',
      'Document Migration: Reorganize legacy folder structures into a new, cleaner hierarchy'
    ],
    configurationOptions: [
      {
        name: 'Include Subfolders Toggle',
        description: 'Enable or disable recursive scanning of nested directories'
      },
      {
        name: 'Exclusion Patterns',
        description: 'Specify folder names or patterns to skip during scanning (e.g., node_modules, .git, __pycache__)'
      },
      {
        name: 'File Limit',
        description: 'Maximum number of files to process in a single sort operation for optimal performance'
      },
      {
        name: 'Default Exclusions',
        description: 'Pre-configured patterns for common system and development folders that are excluded by default'
      }
    ],
    bestPractices: [
      'Start with a preview to verify the file count is reasonable',
      'Add exclusion patterns for any large folders you don\'t want processed',
      'Use specific exclusions for development projects (node_modules, vendor, .git)',
      'Consider sorting smaller sections first when dealing with very large directory trees',
      'Review the sorting preview carefully when processing files from multiple folder levels',
      'Back up important nested structures before major reorganizations'
    ],
    relatedTerms: ['folder-structure', 'automated-file-sorting', 'file-categorization'],
    lastUpdated: '2025-01-07',
    faq: [
      {
        question: 'What folders are excluded by default?',
        answer: 'Sortio automatically excludes common system and development folders including: .git, node_modules, __pycache__, .DS_Store, .Spotlight-V100, .Trashes, vendor, and build directories. You can view and modify these exclusions in the settings.'
      },
      {
        question: 'Is there a limit to how many files can be sorted?',
        answer: 'For optimal performance, recursive sorting has a file limit (typically 1000 files). This ensures sorting completes quickly and the preview remains manageable. For larger directories, consider sorting in sections or adding exclusion patterns to reduce the file count.'
      },
      {
        question: 'Will files from subfolders lose their original location information?',
        answer: 'Sortio\'s activity logging records the original path of every file moved, so you can always see where files came from. Additionally, the backup feature preserves your original folder structure before any changes are made.'
      },
      {
        question: 'Can I sort subfolders while keeping some folder structure?',
        answer: 'Yes. Your sorting prompt can instruct Sortio to maintain or create folder hierarchies. For example, "Organize files by type, keeping project subfolders intact" will create a structure that respects your original organization while grouping by file type.'
      }
    ]
  },
  {
    id: 'rule-discovery',
    slug: 'rule-discovery',
    title: 'Rule Discovery',
    category: 'Features',
    shortDescription: 'Browse and import community-created rulepacks tailored to your profession or workflow.',
    definition: 'Rule Discovery is a community marketplace where users can browse, vote on, import, and publish reusable sorting rule collections called "rulepacks" organized by industry verticals like Legal, Students, Developers, Designers, and more.',
    whatIs: 'Rule Discovery transforms Sortio from a personal tool into a community platform. Instead of building sorting rules from scratch, you can browse a library of professionally-created and community-contributed rulepacks designed for specific use cases. Lawyers can import legal document organizers, students can use study material sorters, developers can grab code file organizers—all ready to use instantly. You can also share your own rules to help others.',
    howItWorks: 'The Discover tab in Sortio shows all available rulepacks organized by category. Each rulepack contains multiple rules that work together for a specific workflow. You can preview the rules before importing, see how many people have used them, and read descriptions of what they do. When you import a rulepack, Sortio automatically adjusts the paths for your system—a rule designed on Windows works perfectly on Mac and vice versa. Community engagement through voting helps surface the best rulepacks.',
    keyCapabilities: [
      'Browse rulepacks by industry: Legal, Students, Developers, Designers, Photography, Finance, General',
      'Preview rules before importing to understand exactly what they do',
      'One-click import adds rules to your library with path resolution',
      'Vote on rulepacks to help others discover the best ones',
      'Get notified when imported rulepacks are updated by creators',
      'Publish your own rules to help others in your industry',
      'Staff picks highlight professionally curated, high-quality packs',
      'Cross-platform compatibility with automatic path translation'
    ],
    gettingStarted: [
      'Open Sortio and navigate to the Discover tab',
      'Browse rulepacks by category or search for specific workflows',
      'Click on a rulepack to preview its rules and see details',
      'Click "Import Rulepack" to add the rules to your library',
      'Find your new rules in the Rules tab, ready to use',
      'Vote on rulepacks you find useful to help others discover them'
    ],
    useCases: [
      'New Law Firm Setup: Import the Legal Document Organizer rulepack and immediately have rules for sorting contracts, pleadings, correspondence, and client files',
      'Student Semester Start: Import the Student Study Materials pack to automatically organize lectures, assignments, research papers, and notes by course',
      'Developer Onboarding: Get the Developer Workspace Organizer to sort code files, documentation, and project assets across multiple languages',
      'Photography Workflow: Import Photo Library Manager to organize shots by date, separate RAW files, and manage edited exports',
      'Team Standardization: Share a custom rulepack across your team so everyone organizes files the same way'
    ],
    configurationOptions: [
      {
        name: 'Browse Filters',
        description: 'Filter rulepacks by vertical (Legal, Students, etc.), sort by popularity, downloads, or newest'
      },
      {
        name: 'Staff Picks',
        description: 'View curated collections of high-quality, verified rulepacks recommended by the Sortio team'
      },
      {
        name: 'Search',
        description: 'Search rulepacks by name, description, or tags to find exactly what you need'
      },
      {
        name: 'Update Notifications',
        description: 'Get notified when creators update rulepacks you\'ve imported so you can apply improvements'
      }
    ],
    bestPractices: [
      'Preview rules before importing to ensure they match your workflow',
      'Start with Staff Picks for professionally curated, high-quality packs',
      'Vote on packs you find useful to help the community',
      'Check for updates periodically to get creator improvements',
      'Customize imported rules to better fit your specific needs',
      'Consider publishing your own rules to help others in your industry'
    ],
    relatedTerms: ['automated-file-sorting', 'folder-structure', 'file-categorization'],
    lastUpdated: '2026-01-18',
    faq: [
      {
        question: 'How many rulepacks can I import?',
        answer: 'Free users can browse and preview all rulepacks but cannot import. Pro users have unlimited imports. You can always upgrade if you need more.'
      },
      {
        question: 'Will imported rules work on my system?',
        answer: 'Yes. Rulepacks use placeholder paths like {DOWNLOADS} and {DOCUMENTS} that automatically resolve to your actual system folders. A rule created on Windows will work perfectly on Mac and vice versa.'
      },
      {
        question: 'Can I modify rules after importing?',
        answer: 'Absolutely. Imported rules become part of your personal library and you can edit, customize, or delete them like any other rule. Your changes won\'t affect the original rulepack.'
      },
      {
        question: 'How do I publish my own rulepack?',
        answer: 'Click "Publish Rulepack" in the Discover tab, select which of your rules to include, add a title and description, and submit. All submissions go through content moderation to ensure quality.'
      },
      {
        question: 'What happens when a rulepack creator updates their pack?',
        answer: 'You\'ll see a notification badge on the Discover tab. You can view the update notes and choose to apply the update, which will sync the latest rule definitions while preserving any personal customizations you\'ve made.'
      }
    ]
  },
  {
    id: 'ai-rule-builder',
    slug: 'ai-rule-builder',
    title: 'AI Rule Builder',
    category: 'Features',
    shortDescription: 'Describe how you want files sorted in plain English, and AI builds the rules for you.',
    definition: 'The AI Rule Builder uses natural language processing to convert your descriptions of how files should be organized into fully functional sorting rules—no technical knowledge required.',
    whatIs: 'The AI Rule Builder is your conversational assistant for creating sorting rules. Instead of manually configuring conditions and actions, you simply describe what you want in plain English: "Move PDF invoices to my Finance folder" or "Sort photos by year into dated subfolders." The AI understands your intent and generates properly configured rules that you can use immediately or refine further.',
    howItWorks: 'When you start the AI Rule Builder, you enter a chat-like interface where you describe your sorting needs. The AI analyzes your request, identifies file types, conditions, and destinations, then generates a complete rule with proper conditions and actions. You can preview the rule, ask the AI to refine it (up to 10 conversation turns), or switch to manual editing if you want fine-grained control. Once satisfied, save the rule to your library.',
    keyCapabilities: [
      'Natural language input—describe rules in plain English',
      'Intelligent parsing of file types, patterns, and destinations',
      'Automatic condition and action generation',
      'Conversational refinement with up to 10 turns',
      'Preview generated rules before saving',
      'Switch to manual editor for fine-tuning',
      'Feedback system to improve AI quality',
      'Support for complex multi-condition rules'
    ],
    gettingStarted: [
      'Go to the Rules tab and click "Create Rule"',
      'Select "AI Rule Builder" instead of manual configuration',
      'Describe what you want in plain English (e.g., "Sort invoices by vendor name")',
      'Review the generated rule preview',
      'Refine by continuing the conversation or click "Edit Manually" for precise control',
      'Save the rule to start using it immediately'
    ],
    useCases: [
      'Quick Rule Creation: "Move all screenshots from Downloads to a Screenshots folder" — done in seconds',
      'Complex Patterns: "Sort documents by client name found in the filename, creating subfolders for each client"',
      'Date-Based Organization: "Organize photos into Year/Month folders based on when they were taken"',
      'Extension Filtering: "Move all video files larger than 100MB to my Videos/Large folder"',
      'Content-Based Rules: "Sort invoices into folders by vendor name extracted from the PDF content"'
    ],
    configurationOptions: [
      {
        name: 'Conversation Mode',
        description: 'Chat with the AI to describe and refine your rule through natural dialogue'
      },
      {
        name: 'Preview Panel',
        description: 'See the generated rule structure before saving to verify it matches your intent'
      },
      {
        name: 'Manual Edit Switch',
        description: 'Toggle to manual editor at any time for precise condition and action control'
      },
      {
        name: 'Feedback Buttons',
        description: 'Rate generated rules with thumbs up/down to help improve AI quality'
      }
    ],
    bestPractices: [
      'Be specific about file types and destinations in your description',
      'Include folder paths like "my Documents folder" or "Desktop"',
      'Mention any patterns: "files that contain \'invoice\' in the name"',
      'Use conversational refinement: "Make it also include receipts"',
      'Preview before saving to verify the rule matches your intent',
      'Give feedback (thumbs up/down) to help improve the AI'
    ],
    relatedTerms: ['automated-file-sorting', 'file-categorization'],
    lastUpdated: '2026-01-18',
    faq: [
      {
        question: 'What kinds of rules can the AI create?',
        answer: 'The AI can create rules based on file names, extensions, sizes, dates, and content. It can move files, rename them, and organize into folder structures. Complex multi-condition rules are supported.'
      },
      {
        question: 'Is the AI Rule Builder available on all plans?',
        answer: 'The AI Rule Builder is available on every plan. Generating a rule with AI uses a small part of your AI allowance: the free trial includes a one-time allowance, while Pro and Team include monthly allowances measured over your last 30 days of AI activity. Running the rules themselves is always free.'
      },
      {
        question: 'Can I edit AI-generated rules manually?',
        answer: 'Yes. Click "Edit Manually" at any time to switch to the traditional rule editor where you have complete control over conditions, operators, and actions.'
      },
      {
        question: 'What if the AI doesn\'t understand what I want?',
        answer: 'You can refine your request in conversation—the AI remembers context from previous messages. If it still doesn\'t match, switch to manual editing or try rephrasing with more specific details about file types and destinations.'
      },
      {
        question: 'Does the AI process my actual files?',
        answer: 'No. Rule generation only analyzes your text description, and the rules themselves run locally on your machine. Other features differ: AI sorting sends filenames and folder paths to our cloud, and turning on content analysis also sends excerpts of file contents. In Offline Mode (a local model such as Ollama) none of that leaves your machine.'
      }
    ]
  },
  {
    id: 'deterministic-rules',
    slug: 'deterministic-rules',
    title: 'Deterministic Rules Engine',
    category: 'Features',
    shortDescription: 'Create precise IF/THEN rules with exact conditions for predictable, repeatable file organization.',
    definition: 'The Deterministic Rules Engine provides a user-configurable automation system where you define exact conditions and actions—same inputs always produce identical outputs, with no AI interpretation.',
    whatIs: 'While AI sorting handles nuanced organization, sometimes you need precise, predictable control. The Deterministic Rules Engine lets you create IF/THEN rules with exact conditions: if a filename contains "invoice" and the file is a PDF, move it to Finance/Invoices. These rules execute the same way every time, making them ideal for compliance workflows, automated pipelines, and situations where consistency is critical.',
    howItWorks: 'You build rules using a visual Rule Builder with three components: conditions (what files to match), logic operators (how to combine conditions), and actions (what to do with matches). Conditions support comparators like equals, contains, regex, numeric ranges, and date-based filters. You can nest condition groups with AND/OR/NOT logic for complex matching. Rules run in priority order, and you can preview results with dry-run mode before applying changes.',
    keyCapabilities: [
      'IF/THEN automation with precise condition matching',
      'Boolean logic with AND, OR, and NOT operators',
      'Nested condition groups for complex scenarios',
      '20+ comparators: equals, contains, regex, numeric ranges, date filters',
      'Multiple action types: move, rename, tag, and more',
      'Priority-based execution order',
      'Dry-run preview mode to test before applying',
      'Run rules before AI, after AI, or standalone'
    ],
    gettingStarted: [
      'Open Sortio and go to the Rules tab',
      'Click "Create Rule" and choose the visual Rule Builder',
      'Set a name and priority for your rule',
      'Add conditions using the condition builder',
      'Combine conditions with AND/OR/NOT as needed',
      'Define actions (move, rename, etc.)',
      'Use dry-run to preview which files would match',
      'Enable the rule to start processing files'
    ],
    useCases: [
      'Invoice Processing: PDFs containing "invoice" automatically move to Finance/{year}/Invoices',
      'Screenshot Management: Files starting with "Screenshot" go to Screenshots/{date} with timestamp rename',
      'Large File Archival: Files over 100MB modified in the last 30 days move to Archive',
      'Extension-Based Sorting: All .psd files to Design/Source, all .jpg to Design/Exports',
      'Compliance Workflows: Ensure specific file types always land in designated folders'
    ],
    configurationOptions: [
      {
        name: 'Condition Builder',
        description: 'Visual interface to build conditions with field, comparator, and value'
      },
      {
        name: 'Logic Operators',
        description: 'Combine conditions with ALL (AND), ANY (OR), or NONE (NOT) logic'
      },
      {
        name: 'Priority',
        description: 'Set execution order—lower numbers run first'
      },
      {
        name: 'Execution Mode',
        description: 'Run rules before AI sorting, after AI sorting, or standalone'
      },
      {
        name: 'Dry-Run Preview',
        description: 'See exactly which files would be affected before applying'
      }
    ],
    bestPractices: [
      'Use dry-run mode to verify rules before enabling',
      'Start with simple conditions and add complexity as needed',
      'Set priorities carefully when rules might overlap',
      'Combine with AI sorting for the best of both worlds',
      'Use regex comparators for advanced pattern matching',
      'Review the activity log to verify rules are working as expected'
    ],
    relatedTerms: ['automated-file-sorting', 'automate-file-organization'],
    lastUpdated: '2026-01-23',
    faq: [
      {
        question: 'How is this different from AI sorting?',
        answer: 'AI sorting interprets your intent and makes intelligent decisions about file organization. Deterministic rules follow exact conditions you specify—same inputs always produce identical outputs. Use AI for flexible, context-aware sorting; use deterministic rules when you need guaranteed, repeatable results.'
      },
      {
        question: 'Can I combine deterministic rules with AI sorting?',
        answer: 'Yes. You can configure rules to run before AI sorting (to handle specific files first), after AI sorting (to post-process results), or standalone. This gives you precise control over critical files while letting AI handle everything else.'
      },
      {
        question: 'What comparators are available?',
        answer: 'The engine includes 20+ comparators: equals, not equals, contains, starts with, ends with, matches regex, greater than, less than, between, in list, modified within, has tag, exists, and more.'
      },
      {
        question: 'Can I test rules without moving files?',
        answer: 'Yes. Dry-run mode shows you exactly which files match your conditions and what actions would be taken—without actually moving or modifying anything. This lets you refine rules safely before enabling them.'
      }
    ]
  }
];

export const featureCategories = ['All', 'Features', 'Security', 'Platform'];

export const getFeatureBySlug = (slug) => {
  return featuresData.find(f => f.slug === slug);
};

export const getFeaturesByCategory = (category) => {
  if (category === 'All') return featuresData;
  return featuresData.filter(f => f.category === category);
};

export const searchFeatures = (query) => {
  const lowerQuery = query.toLowerCase();
  return featuresData.filter(f =>
    f.title.toLowerCase().includes(lowerQuery) ||
    f.shortDescription.toLowerCase().includes(lowerQuery) ||
    f.category.toLowerCase().includes(lowerQuery)
  );
};
